import {
  Input,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { Modal } from '@fortress-technology-solutions/fortress-component-library/Molecules_Fortress';
import {
  Grid,
  RadioGroup,
  SingleSelect,
  Stepper,
} from '@fortress-technology-solutions/fortress-component-library/Molecules';
import { DatePicker } from '@fortress-technology-solutions/fortress-component-library/Organisms';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Controller, useForm } from 'react-hook-form';
import { toastr } from 'react-redux-toastr';
import { rentForecastExist } from '../utils';
import messages from './messages';
import rentForecastMessages from '../messages';
import useIntl from '../../../hooks/useIntl';
import useSelectedProperty from '../../../hooks/useSelectedProperty';
import { createBudget } from './hooks.useQuery';
import { useDropdownOptions, useFieldRules, validateForm } from './hooks.form';
import { ACTIVE_STEP_INDEX_FIRST, ACTIVE_STEP_INDEX_LAST } from './constants';
import CurrentImage from '../../../img/rentForecast-wizard-tooltip-current.png';
import ProposedImage from '../../../img/rentForecast-wizard-tooltip-proposed.png';
import ActualImage from '../../../img/rentForecast-wizard-tooltip-actual.png';
import CommentsImage from '../../../img/rentForecast-wizard-tooltip-comments.png';
import { AlertInfoIcon } from '@fortress-technology-solutions/fortress-component-library/Icons';
import { Tooltip } from '@fortress-technology-solutions/fortress-component-library/Atoms';

type Props = {
  open: boolean,
  closeModal: () => void,
  rentForecast: any,
  reloadRentForecastList: () => void,
  rentForecastsData: Object,
};

const BuildBudget = ({
  open,
  closeModal,
  rentForecast,
  reloadRentForecastList,
  rentForecastsData,
}: Props) => {
  const intl = useIntl();
  const selectedProperty = useSelectedProperty();
  const [submitting, setSubmitting] = useState(false);
  const [activeStep, setActiveStep] = useState(ACTIVE_STEP_INDEX_FIRST);
  const { control, ...ReactHookFormProps } = useForm({
    mode: 'onBlur',
    defaultValues: {
      budgetName: null,
      budgetType: null,
      budgetPeriod: null,
      currentBudgetYear: null,
      actualBudgetFrom: null,
      actualBudgetTo: null,
      proposedBudgetFill: null,
      commentsFill: null,
    },
  });
  const dropdownOptions = useDropdownOptions(rentForecast);
  const fieldRules = useFieldRules();
  const [actualDateFrom, actualDateTo] = ReactHookFormProps.watch([
    'actualBudgetFrom',
    'actualBudgetTo',
  ]);

  const onClose = (ignoreSubmitting: boolean = false) => {
    if (!ignoreSubmitting && submitting) {
      return;
    }

    closeModal();

    ReactHookFormProps.reset();
    setActiveStep(ACTIVE_STEP_INDEX_FIRST);
  };

  const onBack = () => {
    if (activeStep === ACTIVE_STEP_INDEX_FIRST) {
      onClose();
    } else {
      setActiveStep(activeStep - 1);
    }
  };

  const onContinue = async () => {
    const valid = await validateForm(ReactHookFormProps, activeStep);

    if (!valid) {
      return;
    }

    if (activeStep === ACTIVE_STEP_INDEX_LAST) {
      await submit();
    } else {
      setActiveStep(activeStep + 1);
    }
  };

  const submit = async () => {
    setSubmitting(true);

    try {
      const values = ReactHookFormProps.getValues();

      if (
        rentForecastExist(
          values.budgetName,
          values.currentBudgetYear,
          rentForecastsData.rentForecasts,
        )
      ) {
        toastr.error(
          intl.formatMessage(rentForecastMessages.error),
          intl.formatMessage(rentForecastMessages.forecastDuplicateError),
          { timeOut: 0, showCloseButton: true },
        );
      } else {
        values.proposedBudgetFill = values.proposedBudgetFill === 'yes';
        values.intacctYTDActuals = values.intacctYTDActuals === 'yes';
        values.actualBudgetFrom = values.actualBudgetFrom.format('YYYY-MM-DD');
        values.actualBudgetTo = values.actualBudgetTo.format('YYYY-MM-DD');

        await createBudget(
          selectedProperty.organizationId,
          selectedProperty.id,
          rentForecast.id,
          values,
        );

        onClose(true);
        reloadRentForecastList();

        toastr.success(
          intl.formatMessage(rentForecastMessages.success),
          intl.formatMessage(messages.toastBuildSuccess),
        );
      }
    } catch (error) {
      console.error(error);
      toastr.error(
        intl.formatMessage(rentForecastMessages.error),
        intl.formatMessage(messages.toastBuildError),
        { timeOut: 0, showCloseButton: true },
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={intl.formatMessage(messages.generateReportTitle)}
      actionsProps={[
        {
          children:
            activeStep === ACTIVE_STEP_INDEX_FIRST
              ? intl.formatMessage(rentForecastMessages.cancel)
              : intl.formatMessage(messages.buttonBack),
          onClick: onBack,
          disabled: submitting,
          variant: 'Cancel',
        },
        {
          children:
            activeStep === ACTIVE_STEP_INDEX_LAST
              ? intl.formatMessage(messages.buttonContinue)
              : intl.formatMessage(messages.buttonNext),
          onClick: onContinue,
          submitButton: true,
          isSubmitting: submitting,
        },
      ]}
      ModalProps={{
        PaperProps: {
          sx: {
            width: {
              xs: 'calc(100% - 32px)',
              sm: 540,
            },
            maxHeight: 'calc(100vh - 100px)',
          },
        },
      }}
    >
      <Typography variant="bodySmall">
        <FormattedMessage {...messages.generateReportSubTitle} />
      </Typography>

      <Stepper
        alternativeLabel={true}
        activeStep={activeStep}
        steps={[{ label: { text: 'Budget' } }, { label: { text: 'Report' } }]}
        sx={{
          marginBottom: '16px',
        }}
      />
      <form noValidate>
        {activeStep === 0 && (
          <Grid container rowSpacing={2}>
            <Grid item xs={12}>
              <Controller
                name="budgetName"
                control={control}
                rules={fieldRules.budgetName}
                render={({ field, fieldState }) => (
                  <Input
                    {...field}
                    label={intl.formatMessage(messages.inputLabelBudgetName)}
                    error={!!fieldState.error}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="budgetType"
                control={control}
                rules={fieldRules.budgetType}
                render={({ field, fieldState }) => (
                  <SingleSelect
                    {...field}
                    options={dropdownOptions.budgetTypeOptions}
                    label={intl.formatMessage(messages.dropdownLabelBudgetType)}
                    error={!!fieldState.error}
                    helperText={intl.formatMessage(
                      messages.dropdownHelperTextBudgetType,
                    )}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="budgetPeriod"
                control={control}
                rules={fieldRules.budgetPeriod}
                render={({ field, fieldState }) => (
                  <SingleSelect
                    {...field}
                    options={dropdownOptions.budgetPeriodOptions}
                    label={intl.formatMessage(
                      messages.dropdownLabelBudgetPeriod,
                    )}
                    error={!!fieldState.error}
                    helperText={intl.formatMessage(
                      messages.dropdownHelperTextBudgetPeriod,
                    )}
                  />
                )}
              />
            </Grid>
          </Grid>
        )}
        {activeStep === 1 && (
          <Grid container rowSpacing={2}>
            <Grid item xs={12}>
              <Controller
                name="currentBudgetYear"
                control={control}
                rules={fieldRules.currentBudgetYear}
                render={({ field, fieldState }) => (
                  <SingleSelect
                    {...field}
                    disabled={submitting}
                    error={!!fieldState.error}
                    options={dropdownOptions.currentBudgetOptions}
                    label={intl.formatMessage(
                      messages.dropdownLabelCurrentBudget,
                    )}
                    InputLabelProps={{
                      TooltipProps: {
                        title: (
                          <img src={CurrentImage} alt="Current column image" />
                        ),
                        children: <AlertInfoIcon />,
                        placement: 'top',
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              {/*This style will make the tooltip to be next to label*/}
              <Typography
                variant="labelLarge"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <FormattedMessage {...messages.datePickerActualBudgetRange} />
                <Tooltip
                  title={<img src={ActualImage} alt="Actual column image" />}
                  placement="top"
                >
                  {/*We have to force icon to have same size as the other field labels*/}
                  <AlertInfoIcon sx={{ fontSize: 18 }} />
                </Tooltip>
              </Typography>
              <Grid container columnSpacing={2}>
                <Grid item xs={6}>
                  <Controller
                    name="actualBudgetFrom"
                    control={control}
                    rules={fieldRules.actualBudgetFrom}
                    render={({ field, fieldState }) => (
                      <DatePicker
                        {...field}
                        disabled={submitting}
                        placeholder="From"
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        maxDate={actualDateTo}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Controller
                    name="actualBudgetTo"
                    control={control}
                    rules={fieldRules.actualBudgetTo}
                    render={({ field, fieldState }) => (
                      <DatePicker
                        {...field}
                        disabled={submitting}
                        placeholder="To"
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        minDate={actualDateFrom}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="proposedBudgetFill"
                control={control}
                rules={fieldRules.proposedBudgetFill}
                render={({ field, fieldState }) => (
                  <RadioGroup
                    {...field}
                    disabled={submitting}
                    label={intl.formatMessage(
                      messages.radioProposedBudgetFill,
                      { year: new Date().getFullYear() },
                    )}
                    options={[
                      { label: 'Yes', value: 'yes' },
                      { label: 'No', value: 'no' },
                    ]}
                    row={true}
                    error={!!fieldState.error}
                    helperText={intl.formatMessage(
                      messages.radioProposedBudgetFillHelperText,
                    )}
                    InputLabelProps={{
                      TooltipProps: {
                        title: (
                          <img
                            src={ProposedImage}
                            alt="Proposed column image"
                          />
                        ),
                        children: <AlertInfoIcon />,
                        placement: 'top',
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="intacctYTDActuals"
                control={control}
                rules={fieldRules.intacctYTDActuals}
                render={({ field, fieldState }) => (
                  <RadioGroup
                    {...field}
                    disabled={submitting}
                    label={intl.formatMessage(messages.radioIntacctYTDActuals)}
                    options={[
                      { label: 'Yes', value: 'yes' },
                      { label: 'No', value: 'no' },
                    ]}
                    row={true}
                    error={!!fieldState.error}
                    helperText={null}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="commentsFill"
                control={control}
                rules={fieldRules.commentsFill}
                render={({ field, fieldState }) => (
                  <SingleSelect
                    {...field}
                    disabled={submitting}
                    error={!!fieldState.error}
                    label={intl.formatMessage(messages.dropdownLabelComments)}
                    options={dropdownOptions.commentsOptions}
                    InputLabelProps={{
                      TooltipProps: {
                        title: (
                          <img
                            src={CommentsImage}
                            alt="Comments column image"
                          />
                        ),
                        children: <AlertInfoIcon />,
                        placement: 'top',
                      },
                    }}
                    sx={{
                      mt: 1, // Field above has helper text and makes the spacing look inconsistent
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        )}
      </form>
    </Modal>
  );
};

export default BuildBudget;
